/**
 * PersonalityFields — Código Interno (Eneagrama) self-select.
 *
 * Stateless field component. Caller manages state and persistence into
 * `user_business_context.enneagram_type / enneagram_wing / enneagram_source`.
 *
 * UI: 9 cards in a responsive grid. User picks a primary type (required for
 * the field to have meaning) and optionally an asa (wing) — adjacent type.
 *
 * Front-end mirrors the backend KB at `supabase/functions/_shared/eneatypes.ts`.
 * For UI brevity we reuse the lighter PLAYBOOKS catalog already curated for
 * Decodificar Express.
 */

import { useMemo } from "react";
import { CheckCircle, Lightbulb } from "@phosphor-icons/react";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { PLAYBOOKS } from "@/features/decodificar/express/playbooks";
import type { EnneagramTypeId, EnneagramSource } from "@/types/supabase";

export interface PersonalityValue {
  enneagram_type: EnneagramTypeId | null;
  enneagram_wing: EnneagramTypeId | null;
  enneagram_source: EnneagramSource | null;
}

export interface PersonalityFieldsProps {
  value: PersonalityValue;
  onChange: (next: PersonalityValue) => void;
}

const ALL_TYPES: EnneagramTypeId[] = [1, 2, 3, 4, 5, 6, 7, 8, 9];

const TEST_URL = "https://rafamedeiros.com/seu-codigo/";

function adjacentTypes(type: EnneagramTypeId): EnneagramTypeId[] {
  // Wings are the two neighbors on the enneagram circle (1-9 connected back to 1).
  const prev = type === 1 ? 9 : ((type - 1) as EnneagramTypeId);
  const next = type === 9 ? 1 : ((type + 1) as EnneagramTypeId);
  return [prev, next];
}

export function PersonalityFields({ value, onChange }: PersonalityFieldsProps) {
  const selectedType = value.enneagram_type;
  const wingOptions = useMemo(
    () => (selectedType ? adjacentTypes(selectedType) : []),
    [selectedType],
  );

  const setType = (type: EnneagramTypeId | null) => {
    if (type == null) {
      onChange({
        enneagram_type: null,
        enneagram_wing: null,
        enneagram_source: null,
      });
      return;
    }
    onChange({
      enneagram_type: type,
      // reset wing if no longer adjacent
      enneagram_wing:
        value.enneagram_wing && adjacentTypes(type).includes(value.enneagram_wing)
          ? value.enneagram_wing
          : null,
      enneagram_source: value.enneagram_source ?? "self_select",
    });
  };

  const setWing = (wing: EnneagramTypeId | null) => {
    onChange({ ...value, enneagram_wing: wing });
  };

  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <Label className="text-base">Seu Código Interno</Label>
        <p className="text-sm text-muted-foreground">
          O Eneagrama identifica padrões de personalidade. Saber o seu ajuda o
          sistema a calibrar feedbacks, roteiros e materiais ao seu estilo de
          comunicação. Opcional.
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {ALL_TYPES.map((id) => {
          const pb = PLAYBOOKS[id];
          const isSelected = selectedType === id;
          return (
            <button
              key={id}
              type="button"
              onClick={() => setType(isSelected ? null : id)}
              className={cn(
                "relative text-left rounded-xl border p-4 transition-all",
                "hover:border-primary/60 hover:shadow-sm",
                isSelected
                  ? "border-primary bg-primary/5 ring-2 ring-primary/30"
                  : "border-border bg-card",
              )}
              aria-pressed={isSelected}
            >
              {isSelected ? (
                <CheckCircle
                  weight="fill"
                  className="absolute top-3 right-3 w-5 h-5 text-primary"
                />
              ) : null}
              <div className="flex items-baseline gap-2 mb-1">
                <span className="font-mono text-xs text-muted-foreground">
                  {pb.code}
                </span>
                <span className="font-semibold">{pb.name}</span>
              </div>
              <div className="text-xs text-muted-foreground italic mb-2">
                {pb.archetype}
              </div>
              <p className="text-sm leading-snug">{pb.shortDescription}</p>
              <p className="mt-2 text-xs text-muted-foreground">
                <span className="opacity-70">Crença:</span> {pb.belief}
              </p>
            </button>
          );
        })}
      </div>

      {selectedType ? (
        <div className="space-y-2 rounded-lg border border-dashed border-border bg-muted/30 p-4">
          <Label htmlFor="enneagram-wing" className="text-sm">
            Asa (opcional)
          </Label>
          <p className="text-xs text-muted-foreground">
            A asa é uma personalidade vizinha que influencia o tipo dominante.
            Marque apenas se reconhece traços do tipo adjacente.
          </p>
          <Select
            value={value.enneagram_wing != null ? String(value.enneagram_wing) : "none"}
            onValueChange={(v) =>
              setWing(v === "none" ? null : (Number(v) as EnneagramTypeId))
            }
          >
            <SelectTrigger id="enneagram-wing" className="max-w-xs">
              <SelectValue placeholder="Sem asa" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">Sem asa</SelectItem>
              {wingOptions.map((id) => {
                const pb = PLAYBOOKS[id];
                return (
                  <SelectItem key={id} value={String(id)}>
                    {pb.code} · {pb.name}
                  </SelectItem>
                );
              })}
            </SelectContent>
          </Select>
        </div>
      ) : null}

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 rounded-lg bg-muted/40 px-4 py-3">
        <div className="flex items-start gap-2 text-sm text-muted-foreground">
          <Lightbulb weight="duotone" className="w-5 h-5 text-primary shrink-0 mt-0.5" />
          <span>
            Não sabe seu tipo? Faça o teste oficial do Instituto Rafa Medeiros
            (135 questões, ~15min).
          </span>
        </div>
        <Button asChild variant="outline" size="sm">
          <a href={TEST_URL} target="_blank" rel="noopener noreferrer">
            Fazer o teste
          </a>
        </Button>
      </div>
    </div>
  );
}
